import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getElection, getElectionResults } from '../services/api';
import './Pages.css';

const ResultsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProof, setShowProof] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch election details
        const electionResponse = await getElection(id);
        setElection(electionResponse.data.election);
        
        // Fetch election results
        const resultsResponse = await getElectionResults(id);
        setResults(resultsResponse.data);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load election results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getElectionTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Single Choice';
      case 2: return 'Multi Choice';
      case 3: return 'Ranked Choice';
      default: return 'Unknown';
    }
  };

  const getCandidateNameById = (candidateId) => {
    if (!election || !election.candidates) return 'Unknown Candidate';
    
    const candidate = election.candidates.find(c => c.id === candidateId);
    return candidate ? candidate.name : 'Unknown Candidate';
  };

  const renderResultsChart = () => {
    if (!results || !results.candidateResults || !election) return null;
    
    const candidateResults = results.candidateResults;
    
    // Calculate total votes
    const totalVotes = candidateResults.reduce((sum, result) => sum + result.count, 0);
    
    // Sort by count in descending order
    const sortedResults = [...candidateResults].sort((a, b) => b.count - a.count);
    
    return (
      <div className="results-chart">
        {sortedResults.map((result) => {
          const candidateName = getCandidateNameById(result.candidateId);
          const percentage = totalVotes > 0 ? (result.count / totalVotes) * 100 : 0;
          
          return (
            <div key={result.candidateId} className="result-bar">
              <div className="result-label">
                <span className="candidate-name">{candidateName}</span>
                <span className="result-count">{result.count} votes</span>
              </div>
              
              <div className="bar-container">
                <div 
                  className="bar-fill"
                  style={{ width: `${percentage}%` }}
                ></div>
                <span className="percentage-label">{percentage.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
        
        <div className="total-votes">
          <span>Total Ballots: {results.totalBallots}</span>
        </div>
      </div>
    );
  };

  const toggleProof = () => {
    setShowProof(!showProof);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading election results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button 
            className="btn btn-outline" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
          <Link to={`/elections/${id}`} className="btn btn-primary">
            Back to Election
          </Link>
        </div>
      </div>
    );
  }

  if (!election || !results) {
    return (
      <div className="not-found">
        <h2>Results Not Available</h2>
        <p>The election results you're looking for are not available.</p>
        <Link to="/elections" className="btn btn-primary">
          View All Elections
        </Link>
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="page-header">
        <button 
          className="btn-back" 
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </button>
        <h1>Results: {election.title}</h1>
      </div>

      <div className="results-content">
        <div className="results-meta card">
          <div className="meta-item">
            <span className="meta-label">Type:</span>
            <span className="meta-value">{getElectionTypeLabel(election.election_type)}</span>
          </div>
          
          <div className="meta-item">
            <span className="meta-label">Total Ballots:</span>
            <span className="meta-value">{results.totalBallots}</span>
          </div>
          
          {election.end_time && (
            <div className="meta-item">
              <span className="meta-label">Ended:</span>
              <span className="meta-value">{new Date(election.end_time).toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="results-detail card">
          <h2>Election Results</h2>
          
          {results.candidateResults && results.candidateResults.length > 0 ? (
            renderResultsChart()
          ) : (
            <div className="no-results">
              <p>No votes have been cast in this election yet.</p>
            </div>
          )}
        </div>

        <div className="verification-section card">
          <div className="verification-header" onClick={toggleProof}>
            <h2>Zero-Knowledge Verification</h2>
            <button className="btn-toggle">
              {showProof ? 'Hide Proof' : 'Show Proof'}
            </button>
          </div>
          
          {showProof && (
            <div className="proof-details">
              <p>
                This election uses zero-knowledge proofs to verify the integrity of the voting process 
                without revealing individual votes.
              </p>
              
              <div className="proof-data">
                <h3>Proof Data</h3>
                <div className="proof-code">
                  <code>{results.proof?.data || 'No proof data available'}</code>
                </div>
                
                {results.proof?.simulated && (
                  <div className="proof-simulated">
                    <p>Note: This proof is simulated for demonstration purposes.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="results-actions">
        <Link to={`/elections/${election.id}`} className="btn btn-outline">
          Back to Election Details
        </Link>
        <Link to="/elections" className="btn btn-primary">
          View All Elections
        </Link>
      </div>
    </div>
  );
};

export default ResultsPage;